import {Component, Input, OnInit} from '@angular/core';
import {MetricSettings} from "../metric-graph-collection/metric-graph-collection.component";
import {DealerMetric} from "../../services/dealer-service";
import {HttpClient} from "@angular/common/http";
import {HTTPCONSTANTS} from "../../http/http-constants";

export interface SearchTrackingMetrics {
  groupedBySoundex: GroupedBySoundex[]
  volumeByDay: VolumeByDay[]
  topTerms: TopTerm[]
  topTypes: TopType[]
  anonymousVsLoggedIn: AnonymousVsLoggedIn[]
  uniqueTermCount: UniqueTermCount[]
  trendingSearchTerms: TrendingSearchTerm[]
}

export interface GroupedBySoundex {
  soundexCode: string
  title: string
  totalSearched: number
  results: Result[]
}

export interface Result {
  term: string
  searchCount: number
}

export interface VolumeByDay {
  day: string
  total: number
  types: Types
}

export interface Types {
  "dateless_***x-"?: number
  "dateless_**x-"?: number
  "dateless_*x-"?: number
  initials: number
  search: number
  dateless_detailed?: number
}

export interface TopTerm {
  search_term: string
  count: number
}

export interface TopType {
  search_type: string
  count: number
}

export interface AnonymousVsLoggedIn {
  anonymous: number
  logged_in: number
  total: number
}

export interface UniqueTermCount {
  unique_terms: number
}

export interface TrendingSearchTerm {
  search_term: string
  search_count: number
}

@Component({
  selector: 'pla-search-tracking-metrics',
  templateUrl: './search-tracking-metrics.component.html',
  styleUrls: ['./search-tracking-metrics.component.scss']
})
export class SearchTrackingMetricsComponent implements OnInit {

  public loading: boolean = true;
  public metrics: MetricSettings[] = [];
  public trendingSearches: TrendingSearchTerm[] = [];
  public topTerms: TopTerm[] = [];
  public uniqueSearches: number;
  public topSearchesTableSize: number = 40;
  public topSearchPageCount: number = 2;
  public topSearches = this.TopSearches(this.topSearchesTableSize, this.topSearchPageCount);

  constructor(private httpClient: HttpClient) { }

  ngOnInit(): void {
    this.fetchTrackingMetrics();
  }

  public TopSearches(maxLength: number = 100, numberOfPages: number = 2): any[] {
    if (this.topSearchesTableSize <= 0) return [];
    if (this.topTerms.length === 0) return [];

    this.topSearchesTableSize = Math.round(maxLength / numberOfPages);
    const terms = this.topTerms.slice(0, maxLength);

    const chunks: TopTerm[][] = [];
    for (let i = 0; i < terms.length; i += this.topSearchesTableSize) {
      chunks.push(terms.slice(i, i + this.topSearchesTableSize));
    }
    return chunks;
  }

  private fetchTrackingMetrics(): void {
    const apiUrl = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/Tracking/search-metrics?days=7`;
    this.httpClient.get<SearchTrackingMetrics>(apiUrl).subscribe({
      next: (data: SearchTrackingMetrics) => this.applyMetrics(data),
      error: () => {
        // Fallback to Plato when PlatexAPI search-metrics fails (e.g. 401, or endpoint not deployed)
        this.httpClient.get<SearchTrackingMetrics>(`${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=search-tracking-all`).subscribe({
          next: (data: SearchTrackingMetrics) => this.applyMetrics(data),
          error: () => {
            this.loading = false;
            this.trendingSearches = [];
            this.topTerms = [];
            this.uniqueSearches = 0;
          }
        });
      }
    });
  }

  private applyMetrics(data: SearchTrackingMetrics): void {
    this.loading = false;
    this.trendingSearches = (data.trendingSearchTerms || []).sort((a, b) => b.search_count - a.search_count).slice(0, 10);
    this.uniqueSearches = data.uniqueTermCount?.[0]?.unique_terms ?? 0;
    this.topTerms = data.topTerms || [];
    this.topSearches = this.TopSearches(this.topSearchesTableSize, this.topSearchPageCount);
    this.metrics = [
      new MetricSettings('Searches', 'insights', 'Searches', 'searches', 'searches', [27, 155, 138], (data.volumeByDay || []).map((dp: VolumeByDay) => {
        return { x: dp.day, y: dp.types?.search ?? 0 };
      }), { current: 'today', previous: 'yesterday', relativeTime: true }),
      new MetricSettings('Search Types', 'signpost', 'Search Types', 'types', 'types', [33, 73, 83], (data.topTypes || []).reduce((acc: { x: string; y: number }[], dp) => {
        const key = dp.search_type?.startsWith('dateless_') ? 'dateless' : dp.search_type;
        const existing = acc.find(i => i.x === key);
        if (existing) existing.y += dp.count;
        else acc.push({ x: key, y: dp.count });
        return acc;
      }, []), { current: 'yesterday', previous: 'previous', relativeTime: true }, 'bar'),
    ];
  }

}
